import { useCallback, useEffect, useMemo, useState } from 'react'
import ratingsApi, { RatingItem } from '../client/ratings-api'
import { useAuth } from '../context/AuthContext'

/**
 * Caller's personal ratings for one media (separate from the TMDB score).
 * For shows the single GET also returns all episode rating rows, so the
 * season page can reuse this hook and read episodeRatings by episode id.
 */
export const useUserRatings = (
  mediaType: 'Movie' | 'Show',
  tmdbMediaId: number | undefined,
  enabled: boolean
) => {
  const [ratings, setRatings] = useState<RatingItem[]>([])
  // Gate the fetch on auth being fully READY (token wired up), not just the
  // caller's enabled flag - which is usually isAuthenticated and can be true
  // before a token exists on a fresh tab. Firing early 401s and, historically,
  // triggered the sign-in redirect loop.
  const { isAuthReady } = useAuth()

  const fetchRatings = useCallback(async () => {
    if (!enabled || !isAuthReady || !tmdbMediaId) {
      setRatings([])
      return
    }
    try {
      const response = await ratingsApi.getForMedia(
        mediaType === 'Movie' ? 'movie' : 'show',
        tmdbMediaId
      )
      setRatings(response.data)
    } catch (error) {
      console.error('Error fetching ratings:', error)
    }
  }, [mediaType, tmdbMediaId, enabled, isAuthReady])

  useEffect(() => {
    fetchRatings()
  }, [fetchRatings])

  // The movie/show-level rating row (episode rows excluded)
  const mediaRating = useMemo(
    () => ratings.find((r) => r.ratings_MediaType !== 'Episode') ?? null,
    [ratings]
  )

  // Episode id -> rating row, for the season page stars
  const episodeRatings = useMemo(() => {
    const map = new Map<number, RatingItem>()
    ratings.forEach((r) => {
      if (r.ratings_MediaType === 'Episode' && r.ratings_TmdbEpisodeId != null) {
        map.set(r.ratings_TmdbEpisodeId, r)
      }
    })
    return map
  }, [ratings])

  const rateMedia = useCallback(
    async (value: number | null) => {
      if (!tmdbMediaId) return
      try {
        if (value == null) {
          if (mediaRating) await ratingsApi.delete(mediaRating.ratings_Id)
        } else {
          await ratingsApi.upsert({
            Ratings_MediaType: mediaType,
            Ratings_TmdbMediaId: tmdbMediaId,
            Ratings_Value: value,
          })
        }
        await fetchRatings()
      } catch (error) {
        console.error('Error saving rating:', error)
      }
    },
    [mediaType, tmdbMediaId, mediaRating, fetchRatings]
  )

  const rateEpisode = useCallback(
    async (
      tmdbEpisodeId: number,
      seasonNumber: number,
      episodeNumber: number,
      value: number | null
    ) => {
      if (!tmdbMediaId) return
      try {
        if (value == null) {
          const existing = episodeRatings.get(tmdbEpisodeId)
          if (existing) await ratingsApi.delete(existing.ratings_Id)
        } else {
          await ratingsApi.upsert({
            Ratings_MediaType: 'Episode',
            Ratings_TmdbMediaId: tmdbMediaId,
            Ratings_TmdbEpisodeId: tmdbEpisodeId,
            Ratings_SeasonNumber: seasonNumber,
            Ratings_EpisodeNumber: episodeNumber,
            Ratings_Value: value,
          })
        }
        await fetchRatings()
      } catch (error) {
        console.error('Error saving episode rating:', error)
      }
    },
    [tmdbMediaId, episodeRatings, fetchRatings]
  )

  return { mediaRating, episodeRatings, rateMedia, rateEpisode }
}
